import { Component, OnInit } from '@angular/core';
import { AdminService } from '../../../services/admin/admin.service';
import { ApiServiceService } from '../../../services/common/api-service.service';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../../environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-report-manager',
  templateUrl: './report-manager.component.html',
  styleUrls: ['./report-manager.component.scss']
})
export class ReportManagerComponent implements OnInit {
  reports: any[] = [];
  loading = false;
  activeTab = 'ALL'; // 'ALL', 'PENDING', 'REVIEWED', 'ACTION_TAKEN', 'DISMISSED'

  base64Images: { [id: string]: string } = {};
  detailModalUser: any = null;
  loadingDetail = false;

  constructor(
    private adminService: AdminService,
    private apiService: ApiServiceService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadReports();
  }

  loadReports(): void {
    this.loading = true;
    this.adminService.getReportedProfiles().subscribe({
      next: (resp) => {
        this.loading = false;
        if (resp.status === 'success') {
          this.reports = resp.data || [];
          this.loadReportImages();
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Error loading reports:', err);
      }
    });
  }

  get filteredReports(): any[] {
    if (this.activeTab === 'ALL') return this.reports;
    return this.reports.filter(r => r.status === this.activeTab);
  }

  getTabCount(status: string): number {
    if (status === 'ALL') return this.reports.length;
    return this.reports.filter(r => r.status === status).length;
  }

  loadReportImages(): void {
    this.reports.forEach(report => {
      [report.reported_id, report.reporter_id].forEach(user => {
        if (!user || !user._id) return;
        if (this.base64Images[user._id]) return;

        if (user.personal_info?.avatar_Id) {
          const idKey = user.basic_info?.keycloak_id || user.basic_info?.ory_identity_id || user._id;
          const imgPath = `${idKey}/gallery/avatar/${user.personal_info.avatar_Id}`;
          this.apiService.getImageBase64(imgPath).subscribe({
            next: (resp: any) => {
              if (resp && resp.base64Image) {
                const b64 = resp.base64Image;
                this.base64Images[user._id] = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
              }
            },
            error: () => {}
          });
        }
      });
    });
  }

  getProfilePhoto(user: any): string | null {
    if (!user || !user._id) return null;
    if (this.base64Images[user._id]) return this.base64Images[user._id];
    if (user.personal_info?.profile_picture) {
      const pic = user.personal_info.profile_picture;
      if (pic.startsWith('http') || pic.startsWith('data:')) return pic;
      if (pic.length > 200) return `data:image/jpeg;base64,${pic}`;
    }
    return null;
  }

  getInitials(user: any): string {
    if (!user || !user.basic_info) return 'U';
    const first = (user.basic_info.first_name || '').trim();
    const last = (user.basic_info.last_name || '').trim();
    const fChar = first ? first[0] : '';
    const lChar = last ? last[0] : '';
    return (fChar + lChar).toUpperCase() || 'U';
  }

  openDetail(user: any): void {
    if (!user || !user._id) return;
    this.loadingDetail = true;
    this.detailModalUser = user;
    this.adminService.getUserDetail(user._id).subscribe({
      next: res => {
        this.loadingDetail = false;
        if (res.status === 'success' && res.data) {
          this.detailModalUser = res.data;
        }
      },
      error: err => {
        this.loadingDetail = false;
        console.error('[ReportManager.openDetail]', err);
      }
    });
  }

  closeDetailModal(): void {
    this.detailModalUser = null;
  }

  markReviewed(reportId: string): void {
    this.adminService.markReportReviewed(reportId).subscribe({
      next: () => {
        Swal.fire({
          title: 'Reviewed',
          text: 'Report marked as reviewed.',
          icon: 'success',
          confirmButtonColor: '#198754',
          didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
        });
        if (this.selectedReport?._id === reportId) {
          this.selectedReport.status = 'REVIEWED';
        }
        this.loadReports();
      },
      error: (err) => Swal.fire({
        title: 'Error',
        text: err.error?.message || 'Action failed',
        icon: 'error',
        confirmButtonColor: '#b01e3e',
        didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
      })
    });
  }

  dismissReport(reportId: string): void {
    this.adminService.dismissReport(reportId).subscribe({
      next: () => {
        Swal.fire({
          title: 'Dismissed',
          text: 'Report dismissed.',
          icon: 'success',
          confirmButtonColor: '#6c757d',
          didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
        });
        if (this.selectedReport?._id === reportId) {
          this.selectedReport.status = 'DISMISSED';
        }
        this.loadReports();
      },
      error: (err) => Swal.fire({
        title: 'Error',
        text: err.error?.message || 'Action failed',
        icon: 'error',
        confirmButtonColor: '#b01e3e',
        didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
      })
    });
  }

  suspendUser(reportId: string): void {
    Swal.fire({
      title: 'Suspend Candidate Account?',
      text: 'This will immediately hide their profile, prevent login, and block messaging access.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Suspend Account',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
    }).then((result) => {
      if (result.isConfirmed) {
        this.adminService.suspendUserFromReport(reportId).subscribe({
          next: () => {
            Swal.fire({
              title: 'Suspended',
              text: 'Candidate account has been suspended.',
              icon: 'success',
              confirmButtonColor: '#198754',
              didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
            });
            if (this.selectedReport?._id === reportId) {
              this.selectedReport.status = 'ACTION_TAKEN';
              if (this.selectedReport.reported_id) this.selectedReport.reported_id.is_suspended = true;
            }
            this.loadReports();
          },
          error: (err) => Swal.fire({
            title: 'Error',
            text: err.error?.message || 'Action failed',
            icon: 'error',
            confirmButtonColor: '#b01e3e',
            didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
          })
        });
      }
    });
  }

  selectedReport: any = null;
  previousReportsHistory: any[] = [];
  galleryPhotos: string[] = [];
  currentPhotoIndex = 0;

  getReportCountForUser(userId: string): number {
    if (!userId) return 0;
    return this.reports.filter(r => r.reported_id?._id === userId || r.reported_id === userId).length;
  }

  openReviewModal(report: any): void {
    if (!report) return;
    this.selectedReport = report;
    const reportedId = report.reported_id?._id || report.reported_id;
    this.previousReportsHistory = this.reports.filter(r => (r.reported_id?._id || r.reported_id) === reportedId && r._id !== report._id);

    // Build photo gallery array
    this.galleryPhotos = [];
    const mainPhoto = this.getProfilePhoto(report.reported_id);
    if (mainPhoto) {
      this.galleryPhotos.push(mainPhoto);
    }
    // Also check gallery pictures if available
    if (report.reported_id?.gallery?.photos && Array.isArray(report.reported_id.gallery.photos)) {
      report.reported_id.gallery.photos.forEach((p: any) => {
        if (p?.url && !this.galleryPhotos.includes(p.url)) {
          this.galleryPhotos.push(p.url);
        }
      });
    }
    this.currentPhotoIndex = 0;
  }

  closeReviewModal(): void {
    this.selectedReport = null;
    this.previousReportsHistory = [];
    this.galleryPhotos = [];
    this.currentPhotoIndex = 0;
  }

  prevPhoto(): void {
    if (this.galleryPhotos.length <= 1) return;
    this.currentPhotoIndex = (this.currentPhotoIndex - 1 + this.galleryPhotos.length) % this.galleryPhotos.length;
  }

  nextPhoto(): void {
    if (this.galleryPhotos.length <= 1) return;
    this.currentPhotoIndex = (this.currentPhotoIndex + 1) % this.galleryPhotos.length;
  }

  banUser(reportId: string): void {
    Swal.fire({
      title: 'Ban Candidate Permanently?',
      text: 'This will permanently suspend the candidate account and log this violation.',
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Yes, Ban Permanently',
      confirmButtonColor: '#b01e3e',
      cancelButtonColor: '#6c757d',
      didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
    }).then((result) => {
      if (result.isConfirmed) {
        this.adminService.banUserFromReport(reportId).subscribe({
          next: () => {
            Swal.fire({
              title: 'Banned',
              text: 'Candidate account has been banned.',
              icon: 'success',
              confirmButtonColor: '#198754',
              didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
            });
            if (this.selectedReport?._id === reportId) {
              this.selectedReport.status = 'ACTION_TAKEN';
              if (this.selectedReport.reported_id) this.selectedReport.reported_id.is_suspended = true;
            }
            this.loadReports();
          },
          error: (err) => Swal.fire({
            title: 'Error',
            text: err.error?.message || 'Action failed',
            icon: 'error',
            confirmButtonColor: '#b01e3e',
            didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
          })
        });
      }
    });
  }

  restoreUser(reportId: string): void {
    Swal.fire({
      title: 'Restore Candidate Account?',
      text: 'This will restore active login, messaging access, and public visibility.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Restore Account',
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
    }).then((result) => {
      if (result.isConfirmed) {
        this.adminService.restoreUserFromReport(reportId).subscribe({
          next: () => {
            Swal.fire({
              title: 'Restored',
              text: 'Candidate account access has been restored.',
              icon: 'success',
              confirmButtonColor: '#198754',
              didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
            });
            if (this.selectedReport?._id === reportId) {
              if (this.selectedReport.reported_id) this.selectedReport.reported_id.is_suspended = false;
            }
            this.loadReports();
          },
          error: (err) => Swal.fire({
            title: 'Error',
            text: err.error?.message || 'Action failed',
            icon: 'error',
            confirmButtonColor: '#b01e3e',
            didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; }
          })
        });
      }
    });
  }
}